import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { slugify } from '../../utils/slugify';
import useServicePageCms from '../../hooks/useServicePageCms';
import ServicesHero from './ServicesHero';
import ServicePageBody from './ServicePageBody';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import DepartmentIcon from '../../utils/departmentIcons';

const SurgicalClinics = () => {
  const { content } = useServicePageCms('surgical-clinics');

  const { state } = useData();
  const { departments, doctors } = state;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter departments for Surgical Clinics category (only active ones)
  const surgicalDepartments = departments.filter(dept => 
    dept.service_category === 'Surgical Clinics' && 
    (dept.is_active === true || dept.is_active === 1)
  );

  // Filter departments based on search term
  const filteredDepartments = surgicalDepartments.filter(dept =>
    dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dept.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Hero Section */}
      <ServicesHero 
        title={content.title}
        subtitle={content.subtitle}
        serviceType="surgical"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Overview */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {content.introTitle}
          </Typography>
          <Typography variant="h6" paragraph sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
            {content.introText}
          </Typography>
        </Box>

        {/* Search Box */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <TextField
            placeholder="Search surgical departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: '100%', sm: '400px', md: '500px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '25px',
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Surgical Departments */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4}>
            {filteredDepartments.length > 0 ? (
              filteredDepartments.map((department) => (
                <Grid item xs={12} md={6} lg={4} key={department.id}>
                  <Card sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': { 
                      transform: 'translateY(-8px)', 
                      transition: 'transform 0.3s ease',
                      boxShadow: 6
                    }
                  }} onClick={() => navigate(`/department/${slugify(department.name)}`)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <DepartmentIcon iconName={department.icon} sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                          {department.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {department.description}
                      </Typography>
                      
                      {/* Contact Information - Only show phone if enabled */}
                      {department.show_phone && department.phone && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {department.phone}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    {searchTerm ? 'No surgical departments found' : 'Surgical Departments'}
                  </Typography>
                  <Typography color="text.secondary">
                    {searchTerm 
                      ? `No departments match "${searchTerm}". Try different keywords.`
                      : 'Our surgical departments include General Surgery, Cardiac Surgery, Orthopedic Surgery, and Neurosurgery. Each department is equipped with state-of-the-art technology and staffed by experienced surgeons.'
                    }
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>

        <ServicePageBody content={content} inContainer={false} />
      </Container>
    </Box>
  );
};

export default SurgicalClinics; 